import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import { OverviewSkeleton } from './OverviewSkeleton';
import { DetailsGrid } from '../DetailsGrid';
import { useFieldFormatting } from '../hooks/useFieldFormatting';
import { useGetDetailsData } from '../hooks/useGetDetailsData';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { OverviewAccordion } from '../ComponentsOverview/OverviewAccordion';
import { useMemo } from 'react';

type DetailsSectionProps = {
  assemblyId?: string;
};

export const DetailsSection = ({ assemblyId }: DetailsSectionProps) => {
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const assemblyDetailsById = useAtomValue(state.componentDetailsById);
  const assemblyAreaById = useAtomValue(state.componentAreasById);
  const assemblyDataLoading = useAtomValue(state.componentsDetailsLoading);
  const assemblyDetails = useMemo(
    () => assemblyDetailsById?.[assemblyId ?? selectedAssemblyId ?? ''] ?? {},
    [assemblyDetailsById, assemblyId, selectedAssemblyId]
  );
  const assemblyArea = assemblyAreaById?.[assemblyId ?? selectedAssemblyId ?? ''] ?? 0;

  const { getFieldName, displayableFields, getFormattedValueDetails } = useFieldFormatting(
    assemblyDetails,
    assemblyArea,
    assemblyId ?? selectedAssemblyId
  );
  const { reviewSections } = useGetDetailsData(assemblyDetails);

  if (assemblyDataLoading || !assemblyTemplate) {
    return (
      <Stack padding="2.4rem" gap="2.4rem" sx={{ overflowY: 'auto' }}>
        <OverviewSkeleton />
      </Stack>
    );
  }

  return (
    <>
      {reviewSections.map((group) => (
        <OverviewAccordion
          key={group.groupName}
          title={group.groupName}
          defaultExpanded
          details={
            <Grid container spacing="2.6rem" sx={{ width: '100%', p: '4rem', pt: '1.4rem' }}>
              {group.sections.map((section) => (
                <DetailsGrid
                  key={section.title}
                  title={section.title}
                  data={section.data}
                  getFieldName={getFieldName}
                  getFormattedValue={getFormattedValueDetails}
                  displayableFields={displayableFields}
                  allowEditing
                />
              ))}
            </Grid>
          }
        />
      ))}
    </>
  );
};
